/**
 * What to call an organization's billing state in one or two words — the
 * profile chip ("Free trial", "Trial ended", "Pro", "Past due", …) and the
 * onboarding placement chooser, which shows the same phase for EVERY
 * organization a person could put an agent in. One definition so the two can
 * never describe the same pool differently.
 *
 * Client-safe: pure, and the labels are message descriptors the caller renders
 * with its own `i18n`.
 */

import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { trialDaysLeft, type BillingStatus, type BudgetStatus } from "./billing";
import { isMaxLevelPlan, levelOfPlan, subscriptionPlanForDbName, type EnvPlan, type SubscriptionPlan } from "./currency";

const TIER_LABELS: Record<EnvPlan, MessageDescriptor> = {
  starter: msg`Starter`,
  pro: msg`Pro`,
  max: msg`Max`,
};

/** A tier's display name. Total over SubscriptionPlan, not only the tiers on
 *  sale: an existing Starter subscriber still needs their own plan named back
 *  to them long after we stopped offering it. */
export const tierLabel = (plan: SubscriptionPlan): MessageDescriptor => {
  if (!isMaxLevelPlan(plan)) return TIER_LABELS[plan];
  const level = levelOfPlan(plan);
  return msg`Max Level ${level}`;
};

const STATUS_LABELS: Record<BillingStatus["status"], MessageDescriptor> = {
  active: msg`Pro`,
  trialing: msg`Free trial`,
  past_due: msg`Past due`,
  canceled: msg`Canceled`,
  unpaid: msg`Unpaid`,
};

/**
 * The phase label for `billing`.
 *
 * `remaining` defaults to `budget.remaining`; the profile chip passes its own
 * because it keeps a live balance fed by WebSocket updates. A null `budget`
 * means the balance is unknown (a surface rendered without one), and then a
 * trial is never called "ended" on the strength of a zero it does not know.
 */
export function planLabel(
  billing: BillingStatus,
  budget: BudgetStatus | null | undefined,
  remaining: number = budget?.remaining ?? 0,
): MessageDescriptor {
  // The agreement first: it names how the account is billed, which is true for
  // an insider contact too.
  if (billing.agreement) return billing.agreement.quotaMode === "refill" ? msg`Daily quota` : msg`Monthly allowance`;
  if (billing.exempt) return msg`Pro`;
  if (billing.status === "trialing") {
    const daysLeft = trialDaysLeft(billing);
    // Trial ends when the budget is spent OR the window closes — mirror the
    // budget-bounded trial gate, but only claim "ended" when we actually know it.
    const windowClosed = daysLeft !== null && daysLeft <= 0;
    const trialSpent = budget != null && remaining <= 0;
    if (windowClosed || trialSpent) return msg`Trial ended`;
    // Not started yet (lazy start on first message): neutral label, agreeing
    // with the billing page's "starts when you send your first message" copy.
    // Running: the profile dropdown's BudgetMeter shows the live % + tooltip.
    return daysLeft === null ? msg`Free trial` : msg`Trial`;
  }
  // An active subscriber is labelled with their tier — a Max customer called
  // "Pro" in their own header reads as a billing mistake. A plan row that
  // isn't a tier ('champion', 'internal') keeps the long-standing "Pro".
  const tier = billing.status === "active" && budget ? subscriptionPlanForDbName(budget.planName) : null;
  return tier ? tierLabel(tier) : STATUS_LABELS[billing.status];
}
